import React, { useEffect, useState } from 'react'
import { Modal, Table, Spinner } from 'react-bootstrap'

import { Button, Checkbox } from 'src/components'
import { ReservedInventoryItem } from 'src/services/reservedInventory.service'

interface Props {
  open: boolean
  items: ReservedInventoryItem[]
  loadingList: boolean
  submitting: 'release' | 'remove' | null
  onRelease: () => void
  onRemove: () => void
  onClose: () => void
}

const ReservedInventoryModal: React.FC<Props> = ({
  open,
  items,
  loadingList,
  submitting,
  onRelease,
  onRemove,
  onClose,
}) => {
  const [accepted, setAccepted] = useState(false)

  // Resetea el check cada vez que se abre el modal.
  useEffect(() => {
    if (open) setAccepted(false)
  }, [open])

  const busy = submitting !== null
  const actionsDisabled = !accepted || busy || items.length === 0

  return (
    <Modal
      show={open}
      size={'lg'}
      centered
      backdrop={busy ? 'static' : true}
      keyboard={!busy}
      onHide={() => {
        if (!busy) onClose()
      }}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Ajustar inventario reservado</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p>
          Los siguientes productos tienen existencia reservada en la bodega seleccionada.
          El inventario reservado está apartado por procesos como ventas, despachos o
          traslados, y no está disponible para otras operaciones. Puedes{' '}
          <strong>liberarlo</strong> para devolverlo a la existencia disponible, o{' '}
          <strong>eliminarlo</strong> para sacarlo de la bodega mediante una reducción de
          inventario (merma).
        </p>
        <p style={{ color: '#B35796' }}>
          <strong>Advertencia:</strong> liberar o eliminar rompe el vínculo con el proceso
          que originó cada reserva y puede provocar inconsistencias en esas operaciones.
          La acción aplica a <strong>todo</strong> el reservado de la bodega (sin
          selección parcial) y <strong>no se puede revertir</strong>. Procede con cuidado.
        </p>

        {loadingList ? (
          <div className={'d-flex justify-content-center my-4'}>
            <Spinner animation={'border'} variant={'secondary'} />
          </div>
        ) : items.length === 0 ? (
          <p className={'text-center my-4'}>
            No hay inventario reservado en esta bodega.
          </p>
        ) : (
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            <Table striped bordered hover size={'sm'}>
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  background: '#fff',
                  boxShadow: 'inset 0 -1px 0 #dee2e6',
                }}>
                <tr>
                  <th>Productos ({items.length})</th>
                  <th className={'text-right'}>Cantidad reservada</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.productId}>
                    <td>{item.name}</td>
                    <td className={'text-right'}>{item.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <Checkbox
          checked={accepted}
          disabled={busy || items.length === 0}
          changeValue={setAccepted}
          label={
            'Entiendo que esta acción no se puede revertir y puede afectar los procesos que originaron las reservas.'
          }
        />
      </Modal.Body>

      <Modal.Footer>
        <Button
          color={'primary'}
          disabled={actionsDisabled}
          loading={submitting === 'release'}
          onClick={onRelease}>
          Liberar existencia
        </Button>
        <Button
          color={'secondary'}
          disabled={actionsDisabled}
          loading={submitting === 'remove'}
          onClick={onRemove}>
          Eliminar existencia
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ReservedInventoryModal
